import {
  ELEMENT_TYPE,
  type ElementRegistry,
  type ElementRenderer,
  // Basic inputs
  ShortTextInput,
  NameInput,
  LongTextInput,
  NumberInput,
  CurrencyInput,
  SliderInput,
  GeoTag,
  EmailInput,
  PhoneInput,
  UrlInput,
  AddressInput,
  // Selection
  DropdownSelect,
  CheckboxInput,
  MultiChoice,
  RadioCards,
  ToggleSwitch,
  ToggleGroup,
  ColourBands,
  RatingInput,
  CountryInput,
  // Date & time
  DatePicker,
  DateRange,
  TimePicker,
  DateTimePicker,
  // Media & files
  FileUpload,
  ImageUpload,
  Signature,
  FileDownload,
  DisplayImage,
  // Layout containers
  Section,
  Columns,
  Page,
  Pages,
  Tabs,
  Tab,
  // Layout (non-container)
  Heading,
  Paragraph,
  Divider,
  Spacer,
  Logo,
  InfoCard,
  InfoTable,
  RichText,
  LineItems,
  // Entity pickers
  AssetPicker,
  ContactPicker,
  CustomerPicker,
  JobPicker,
  ManagedPropertyPicker,
  ProjectPicker,
  UserPicker,
} from '@eaco/forms-ui-web';

const REQUIRED_PUBLIC_FORM_ELEMENT_TYPES = [
  // Basic inputs
  ELEMENT_TYPE.shortTextInput,
  ELEMENT_TYPE.nameInput,
  ELEMENT_TYPE.longTextInput,
  ELEMENT_TYPE.numberInput,
  ELEMENT_TYPE.emailInput,
  ELEMENT_TYPE.phoneInput,
  ELEMENT_TYPE.urlInput,
  ELEMENT_TYPE.addressInput,

  // Selection
  ELEMENT_TYPE.dropdownSelect,
  ELEMENT_TYPE.checkboxInput,
  ELEMENT_TYPE.multiChoice,
  ELEMENT_TYPE.radioCards,
  ELEMENT_TYPE.toggleSwitch,

  // Date & time
  ELEMENT_TYPE.datePicker,
  ELEMENT_TYPE.dateRange,
  ELEMENT_TYPE.timePicker,
  ELEMENT_TYPE.dateTimePicker,

  // Media & files
  ELEMENT_TYPE.fileUpload,
  ELEMENT_TYPE.imageUpload,
  ELEMENT_TYPE.signature,
  ELEMENT_TYPE.fileDownload,
  ELEMENT_TYPE.displayImage,

  // Layout containers
  ELEMENT_TYPE.section,
  ELEMENT_TYPE.columns,
  ELEMENT_TYPE.page,
  ELEMENT_TYPE.pages,
  ELEMENT_TYPE.tabs,
  ELEMENT_TYPE.tab,

  // Layout (non-container)
  ELEMENT_TYPE.heading,
  ELEMENT_TYPE.paragraph,
  ELEMENT_TYPE.divider,
  ELEMENT_TYPE.spacer,
  ELEMENT_TYPE.logo,

  // Entity pickers
  ELEMENT_TYPE.managedProperty,
  ELEMENT_TYPE.contact,
  ELEMENT_TYPE.job,
  ELEMENT_TYPE.asset,
  ELEMENT_TYPE.user,
  ELEMENT_TYPE.project,
  ELEMENT_TYPE.customer,
] as const;

type RequiredPublicFormElementType =
  (typeof REQUIRED_PUBLIC_FORM_ELEMENT_TYPES)[number];

/**
 * Renderer registry for the apps/forms anonymous public-fill flow.
 * Keys mirror the element-type ids defined in `@eaco/forms-core`.
 *
 * Every renderer is pulled from `@eaco/forms-ui-web` — apps/forms keeps
 * no local element copies. The file/media renderers reach their I/O via
 * the package's FormFileTransport, which this app supplies through
 * `FormsFileTransportProvider`.
 *
 * Differences vs apps/web's webElementRegistry:
 *   - DocumentResponseSetPicker (global response-set picker) is omitted
 *     — editor / authenticated only.
 *   - Editor-only types (configForm pickers, palette etc.) are omitted.
 *
 * The package's `DocumentElement` dispatcher renders a placeholder for
 * any element with an unmapped typeId, so anonymous fillers get a
 * graceful fallback if a form contains an excluded type.
 */
export const formsAppElementRegistry = {
  // Basic inputs
  'short-text-input': ShortTextInput,
  'name-input': NameInput,
  'long-text-input': LongTextInput,
  'number-input': NumberInput,
  currency: CurrencyInput,
  slider: SliderInput,
  'geo-tag': GeoTag,
  'email-input': EmailInput,
  'phone-input': PhoneInput,
  'url-input': UrlInput,
  'address-input': AddressInput,

  // Selection
  'dropdown-select': DropdownSelect,
  'checkbox-input': CheckboxInput,
  'multi-choice': MultiChoice,
  'radio-cards': RadioCards,
  'toggle-switch': ToggleSwitch,
  // Web-registry inputs also offered in the form_document palette — without
  // these an anonymous filler hits the unknown-element error box.
  'toggle-group': ToggleGroup,
  'colour-bands': ColourBands,
  rating: RatingInput,
  country: CountryInput,

  // Date & time
  'date-picker': DatePicker,
  'date-range': DateRange,
  'time-picker': TimePicker,
  'datetime-picker': DateTimePicker,

  // Media & files
  'file-upload': FileUpload,
  'image-upload': ImageUpload,
  signature: Signature,
  'file-download': FileDownload,
  image: DisplayImage,

  // Layout containers
  section: Section,
  columns: Columns,
  page: Page,
  pages: Pages,
  tabs: Tabs,
  tab: Tab,

  // Layout (non-container)
  heading: Heading,
  paragraph: Paragraph,
  divider: Divider,
  spacer: Spacer,
  logo: Logo,
  card: InfoCard,
  table: InfoTable,
  'rich-text': RichText,
  'line-items': LineItems,

  // Entity pickers — server-side authorisation runs off the
  // `publicSlug` carried in FormFillContext, so each picker only ever
  // returns rows the form owner intended to expose on the public form.
  'managed-property': ManagedPropertyPicker,
  contact: ContactPicker,
  job: JobPicker,
  asset: AssetPicker,
  user: UserPicker,
  project: ProjectPicker,
  customer: CustomerPicker,
} satisfies ElementRegistry &
  Record<RequiredPublicFormElementType, ElementRenderer>;
